import cls from 'classnames';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { closeIcon, errorIcon, successIcon, warningIcon } from '../../assets/EditHeader';
import { useAppSelector } from '../../state';
import { SubmitFailedReason } from '../../types';
import { getNpcStatus } from '../Home/NpcList';
import { LocalImage } from '../Image';
import { track } from '../../track';
import { ENpcStatusTrackText } from '../Home';
import { getDialogModelChoice, PAGE } from '.';
import { useEditPageTrack } from '../../hooks/useEditPageTrack';

interface INotificationProps {
  type: 'success' | 'warning' | 'error';
  onClose: () => void;
  styles: Record<string, string>;
  open: boolean;
  autoCloseDuration?: number;
}

export function Notification({
  type,
  onClose,
  styles,
  open,
  autoCloseDuration = 3000,
}: INotificationProps) {
  const { t } = useTranslation();
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    toastStatus,
    toastInfo,
    npcId,
    statusInfo,
    settingInfo: { chat_model_type },
  } = useAppSelector((state) => state.edit);
  const status = getNpcStatus(statusInfo);

  const handleClose = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      timer.current = setTimeout(() => {
        handleClose();
      }, autoCloseDuration);
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [autoCloseDuration, handleClose, open]);

  const iconSrc = useMemo(() => {
    switch (type) {
      case 'success':
        return successIcon;
      case 'warning':
        return warningIcon;
      case 'error':
        return errorIcon;
      default:
        return warningIcon;
    }
  }, [type]);

  const description = useMemo(() => {
    if (toastStatus === 'error' && toastInfo?.status_msg) return toastInfo?.status_msg;
  }, [toastStatus, toastInfo]);

  const getWarningMessage = useMemo(() => {
    if (toastStatus === 'success') {
      return t('publish_success');
    }

    if (toastStatus === 'error') {
      return t('publish_failed');
    }

    if (toastStatus === 'warning') {
      if (toastInfo?.fail_message) return toastInfo?.fail_message;

      switch (toastInfo?.fail_reason) {
        case SubmitFailedReason.IsDemo:
          return t('demo_agent_not_supported');
        case SubmitFailedReason.UnderModeration:
          return t('under_moderation_not_editable');
        default:
          return t('unknown_error');
      }
    }
  }, [toastStatus, t, toastInfo]);

  const { trackParams } = useEditPageTrack();

  useEffect(() => {
    if (open) {
      if (toastStatus === 'success') {
        track.send('publish_npc_success', PAGE, {
          npc_id: npcId,
          ugc_npc_approving_status: ENpcStatusTrackText[status],
          dialog_model_choice: chat_model_type,
          plugin_list: trackParams.plugin_list,
        });
      } else if (toastStatus === 'error') {
        track.send('publish_npc_fail', PAGE, {
          npc_id: npcId,
          ugc_npc_approving_status: ENpcStatusTrackText[status],
          dialog_model_choice: chat_model_type,
          publish_npc_fail_type: toastInfo?.status_msg,
          plugin_list: trackParams.plugin_list,
        });
      }
    }
  }, [chat_model_type, npcId, open, status, toastInfo?.status_msg, toastStatus]);

  if (!open) return null;

  return (
    <div
      className={cls(styles.notification, {
        [styles.notification_success]: type === 'success',
        [styles.notification_info]: type === 'warning',
        [styles.notification_error]: type === 'error',
      })}
    >
      <div className={styles.notificationIcon}>
        <LocalImage width="24" height="24" alt={`${type} icon`} src={iconSrc} />
      </div>
      <div className={styles.notificationContent}>
        <div className={styles.notificationMessage}>{getWarningMessage}</div>
        {description && <div className={styles.notificationDescription}>{description}</div>}
      </div>
      <LocalImage
        width="24"
        height="24"
        alt="close icon"
        onClick={onClose}
        className={styles.closeIcon}
        src={closeIcon}
      />
    </div>
  );
}

export default Notification;
